import React, { useState, useEffect, useRef } from 'react';
import { Heart, MessageCircle, Package, Inbox } from 'react-feather';
import AuthService from '../../services/authService';
import propertyService from '../../services/propertyService';
import AdminUserModal from './AdminUserModal';
import darkIcon from '../../assets/dark-iconp.png';

// Searchable agent dropdown — type to filter by name, agency, or email.
// Drop-in replacement for the old <select> (same value/onChange contract).
const AgentSearchSelect = ({ agents, value, onChange, placeholder = 'Search agents...', autoFocus = false, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const selectedAgent = agents.find(a => a._id === value) || null;

  const agentLabel = (a) =>
    `${a.name}${a.agentInfo?.agency ? ` - ${a.agentInfo.agency}` : ''}`;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? agents.filter(a => {
        const haystack = `${a.name || ''} ${a.agentInfo?.agency || ''} ${a.email || ''}`.toLowerCase();
        return q.split(/\s+/).every(token => haystack.includes(token));
      })
    : agents;

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Keep highlighted option scrolled into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const el = listRef.current.children[highlightIndex];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, isOpen]);

  // Reset highlight when the filtered list changes
  useEffect(() => { setHighlightIndex(0); }, [query]);

  const selectAgent = (agent) => {
    onChange(agent._id);
    setIsOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightIndex]) selectAgent(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={containerRef} className={className} style={{ position: 'relative', minWidth: '220px' }}>
      <input
        type="text"
        className="form-control"
        autoFocus={autoFocus}
        value={isOpen ? query : (selectedAgent ? agentLabel(selectedAgent) : '')}
        placeholder={selectedAgent && !isOpen ? agentLabel(selectedAgent) : placeholder}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        onChange={(e) => { setQuery(e.target.value); if (!isOpen) setIsOpen(true); }}
        onKeyDown={handleKeyDown}
        style={{ width: '100%', boxSizing: 'border-box', cursor: 'text' }}
      />
      {isOpen && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            maxHeight: '240px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '13px', color: '#9ca3af' }}>
              No agents match "{query}"
            </div>
          ) : (
            filtered.map((a, idx) => (
              <div
                key={a._id}
                onMouseDown={(e) => { e.preventDefault(); selectAgent(a); }}
                onMouseEnter={() => setHighlightIndex(idx)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  backgroundColor: idx === highlightIndex ? '#f3f4f6' : (a._id === value ? '#eef2ff' : '#fff'),
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px',
                }}
              >
                <span style={{ fontWeight: 600, color: '#111827' }}>{a.name}</span>
                {a.agentInfo?.agency && (
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>{a.agentInfo.agency}</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const UserAssignmentManager = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination - server-driven
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 30;
  const searchTimeoutRef = useRef(null);
  
  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  // Inline assign-row state (opens directly inside the table cell instead of a modal)
  const [inlineAssignUserId, setInlineAssignUserId] = useState(null);
  const [inlineSelectedAgentId, setInlineSelectedAgentId] = useState('');
  // Header filters (client-side; narrow the current page of filteredUsers)
  const [searchTypeFilter, setSearchTypeFilter] = useState('all'); // 'all' | 'buy' | 'rent'
  const [stateFilter, setStateFilter] = useState('all');            // 'all' | <state code>

  // Buyer details modal state
  const [showBuyerDetailsModal, setShowBuyerDetailsModal] = useState(false);
  const [selectedBuyerForDetails, setSelectedBuyerForDetails] = useState(null);

  // Messaging modal state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedUserForMessage, setSelectedUserForMessage] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Curated matches modal state
  const [showCurateModal, setShowCurateModal] = useState(false);
  const [selectedUserForCurate, setSelectedUserForCurate] = useState(null);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [isCurating, setIsCurating] = useState(false);
  const [existingMatches, setExistingMatches] = useState([]);
  const [curateView, setCurateView] = useState('existing'); // 'existing' or 'create'

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  // Lead tier emoji + colors — kept consistent with AdminUserModal and the question webhook
  const TIER_EMOJI = { flex: '⚪', bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎' };
  const TIER_COLORS = {
    flex:     { bg: '#f3f4f6', text: '#4b5563' },
    bronze:   { bg: '#f5efe6', text: '#8a6d3b' },
    silver:   { bg: '#eef0f2', text: '#5b6470' },
    gold:     { bg: '#fff7d6', text: '#8a6d00' },
    platinum: { bg: '#eaf3fb', text: '#2563eb' },
  };
  const tierLabel = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : null);

  // Fetch users with server-side pagination
  const fetchUsers = async (page = 1, search = '') => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: usersPerPage.toString()
      });
      
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await fetch(`${API_URL}/users/with-assignments?${params}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data);
        setFilteredUsers(data.data);
        setCurrentPage(data.page || 1);
        setTotalPages(data.pages || 1);
        setTotalUsers(data.total || data.data.length);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all agents for assignment dropdown
  const fetchAgents = async () => {
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/admin/agents', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setAgents(data.data);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  // Handle search - debounced server-side search
  const handleSearch = (term) => {
    setSearchTerm(term);
    
    // Debounce the search to avoid too many API calls
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1, term);
    }, 300);
  };

  // Handle page change - fetch from server
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      fetchUsers(newPage, searchTerm);
    }
  };

  // Toggle inline assign row (opens agent selector directly in the table cell)
  const openInlineAssign = (user) => {
    setInlineAssignUserId(user._id);
    setInlineSelectedAgentId(user.assignedAgent?._id || '');
  };
  const closeInlineAssign = () => {
    setInlineAssignUserId(null);
    setInlineSelectedAgentId('');
  };
  // Save assignment from the inline row
  const handleInlineAssign = async (user) => {
    if (!user || !inlineSelectedAgentId) return;
    setIsAssigning(true);
    setError('');
    try {
      const token = AuthService.getToken();
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/users/${user._id}/assign-agent`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ agentId: inlineSelectedAgentId })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Successfully assigned ${user.name} to agent`);
        closeInlineAssign();
        fetchUsers(currentPage, searchTerm);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to assign user');
      }
    } catch (err) {
      setError('Error assigning user: ' + err.message);
    } finally {
      setIsAssigning(false);
    }
  };

  // Open assignment modal
  const openAssignModal = (user) => {
    setSelectedUser(user);
    setSelectedAgentId(user.assignedAgent?._id || '');
    setShowAssignModal(true);
  };

  // Close assignment modal
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedUser(null);
    setSelectedAgentId('');
  };

  // Assign or update user assignment
  const handleAssignment = async () => {
    if (!selectedUser || !selectedAgentId) {
      return;
    }

    setIsAssigning(true);
    setError('');

    try {
      const token = AuthService.getToken();
      console.log('Auth token:', token ? 'Token exists' : 'No token found');
      
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/users/${selectedUser._id}/assign-agent`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agentId: selectedAgentId
        })
      });

      console.log('Response status:', response.status);
      const data = await response.json();

      if (data.success) {
        setSuccess(`Successfully assigned ${selectedUser.name} to agent`);
        closeAssignModal();
        fetchUsers(currentPage, searchTerm); // Refresh the list
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to assign user');
      }
    } catch (err) {
      setError('Error assigning user: ' + err.message);
    } finally {
      setIsAssigning(false);
    }
  };

  // Remove assignment
  const removeAssignment = async (userId, userName) => {
    if (!window.confirm(`Remove agent assignment for ${userName}?`)) {
      return;
    }

    try {
      const token = AuthService.getToken();
      console.log('Remove assignment - Auth token:', token ? 'Token exists' : 'No token found');
      
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/users/${userId}/unassign-agent`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Remove assignment response status:', response.status);
      const data = await response.json();

      if (data.success) {
        setSuccess(`Successfully removed assignment for ${userName}`);
        fetchUsers(currentPage, searchTerm);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to remove assignment');
      }
    } catch (err) {
      setError('Error removing assignment: ' + err.message);
    }
  };

  // ============================================
  // "CS SENDS" — BULK HANDOFF
  //
  // Everyone who joined in the last N days, never swiped once, and has no agent.
  // The people who signed up and bounced, which is exactly who CS wants to ask
  // "what went wrong" — and nobody else is going to.
  //
  // The preview is advisory. The send recomputes the cohort server-side, so the
  // count in the button can differ from what actually goes out if someone
  // swiped while the panel was open. That's correct, and the result summary
  // reports what really happened rather than what was previewed.
  // ============================================
  const [showCsSendsModal, setShowCsSendsModal] = useState(false);
  const [csSendsDays, setCsSendsDays] = useState(7);
  const [csCandidates, setCsCandidates] = useState(null);
  const [csCandidatesLoading, setCsCandidatesLoading] = useState(false);
  const [csBulkRunning, setCsBulkRunning] = useState(false);
  const [csBulkResult, setCsBulkResult] = useState(null);

  const fetchCsCandidates = async (days) => {
    setCsCandidatesLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/admin/users/cs-candidates?days=${days}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();

      if (data.success) {
        setCsCandidates(data.data);
      } else {
        setCsCandidates(null);
        setError(data.error || 'Failed to load CS candidates');
      }
    } catch (err) {
      setCsCandidates(null);
      setError('Error loading CS candidates: ' + err.message);
    } finally {
      setCsCandidatesLoading(false);
    }
  };

  const openCsSends = () => {
    setShowCsSendsModal(true);
    setCsBulkResult(null);
    fetchCsCandidates(csSendsDays);
  };

  const closeCsSends = () => {
    setShowCsSendsModal(false);
    setCsCandidates(null);
    setCsBulkResult(null);
  };

  const changeCsSendsDays = (days) => {
    setCsSendsDays(days);
    setCsBulkResult(null);
    fetchCsCandidates(days);
  };

  const runCsBulkPush = async () => {
    if (!csCandidates || csCandidates.total === 0) return;

    const count = Math.min(csCandidates.total, csCandidates.limit);
    if (!window.confirm(`Send ${count} user${count === 1 ? '' : 's'} to the CS dashboard?`)) {
      return;
    }

    setCsBulkRunning(true);
    setError('');
    setCsBulkResult(null);
    try {
      const response = await fetch(`${API_URL}/admin/users/cs-bulk-push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ days: csSendsDays })
      });
      const data = await response.json();

      if (data.success) {
        setCsBulkResult(data.data);
        // Anyone pushed drops out of the cohort, so re-preview rather than
        // leaving a list on screen that would send zero if clicked again.
        fetchCsCandidates(csSendsDays);
        // The visible page may contain users that just moved — refresh their
        // badges without disturbing the admin's page or search.
        fetchUsers(currentPage, searchTerm);
      } else {
        setError(data.error || 'Bulk push failed');
      }
    } catch (err) {
      setError('Error running bulk push: ' + err.message);
    } finally {
      setCsBulkRunning(false);
    }
  };

  // Open buyer details
  const openBuyerDetails = (user) => {
    setSelectedBuyerForDetails(user);
    setShowBuyerDetailsModal(true);
  };

  // Close buyer details
  const closeBuyerDetails = () => {
    setShowBuyerDetailsModal(false);
    setSelectedBuyerForDetails(null);
  };

  // Pool assign/unassign happens inside AdminUserModal. It calls fetchUsers too,
  // but that's a round trip — patch local state so the row and the still-open
  // modal reflect the new pool immediately.
  const patchUserPool = (userId, assignedPool) => {
    setUsers(prev => prev.map(u => (u._id === userId ? { ...u, assignedPool } : u)));
    setFilteredUsers(prev => prev.map(u => (u._id === userId ? { ...u, assignedPool } : u)));
    setSelectedBuyerForDetails(prev =>
      prev && prev._id === userId ? { ...prev, assignedPool } : prev
    );
  };

  // ============================================
  // CS DASHBOARD HANDOFF
  //
  // A user is invisible to cs.lauft.app until someone clicks the button in the
  // CS column. There is no automatic sync — that's the whole point, so don't
  // "helpfully" wire this into fetchUsers or the assign flow later.
  //
  // Pushing an already-pushed user is a REFRESH: it re-sends the current
  // snapshot (leadStatus, filters, swipe counts) and CS upserts on external_id.
  // The pushedAt date doesn't move, so the badge keeps showing when they were
  // originally handed over.
  //
  // Unlike most of this file's writes, the response is applied to local state
  // instead of triggering a refetch — a full page reload to flip one badge
  // would drop the admin's scroll position mid-list.
  // ============================================
  const [csBusyId, setCsBusyId] = useState(null);

  const patchUserCsSync = (userId, csSync) => {
    setUsers(prev => prev.map(u => (u._id === userId ? { ...u, csSync } : u)));
    setSelectedBuyerForDetails(prev =>
      prev && prev._id === userId ? { ...prev, csSync } : prev
    );
  };

  const pushUserToCs = async (user) => {
    setCsBusyId(user._id);
    setError('');
    try {
      const response = await fetch(`${API_URL}/admin/users/${user._id}/push-to-cs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();

      if (data.success) {
        patchUserCsSync(user._id, {
          pushedAt: data.data.pushedAt,
          pushedBy: data.data.pushedBy,
          lastPushAt: data.data.lastPushAt,
          lastError: null
        });
        setSuccess(`${user.name} is in the CS dashboard`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // 502 means CS rejected or was unreachable — surface the detail rather
        // than a generic failure, because the fix is almost always on that side
        // (missing schema field, bad secret, cold start).
        setError(data.detail ? `${data.error}: ${data.detail}` : (data.error || 'Failed to push user to CS'));
      }
    } catch (err) {
      setError('Error pushing user to CS: ' + err.message);
    } finally {
      setCsBusyId(null);
    }
  };

  const removeUserFromCs = async (user) => {
    if (!window.confirm(`Remove ${user.name} from the CS dashboard? The CSA will lose access to their profile and activity.`)) {
      return;
    }

    setCsBusyId(user._id);
    setError('');
    try {
      const response = await fetch(`${API_URL}/admin/users/${user._id}/push-to-cs`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();

      if (data.success) {
        // Backend clears the local flag before calling CS, so access is already
        // revoked even when CS didn't confirm the delete. Flip the badge either
        // way and just warn about the stale card.
        patchUserCsSync(user._id, { pushedAt: null, pushedBy: null, lastPushAt: null, lastError: null });
        if (data.warning) {
          setError(`${data.warning} — the card may still be visible in CS.`);
        } else {
          setSuccess(`${user.name} removed from the CS dashboard`);
          setTimeout(() => setSuccess(''), 3000);
        }
      } else {
        setError(data.error || 'Failed to remove user from CS');
      }
    } catch (err) {
      setError('Error removing user from CS: ' + err.message);
    } finally {
      setCsBusyId(null);
    }
  };

  // Messaging functions
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setConversations(data.data);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data.conversation);
        setMessages(data.data.messages);
      }
    } catch (err) {
      setError('Error loading conversation: ' + err.message);
    }
  };

  const openMessageModal = async (user) => {
    setSelectedUserForMessage(user);
    setShowMessageModal(true);
    setMessageText('');
    setMessages([]);
    setSelectedConversation(null);
    
    // Fetch conversations if not already loaded
    if (conversations.length === 0) {
      await fetchConversations();
    }
    
    // Check if conversation exists with this admin
    // Add null check for c.user to prevent errors
    const existing = conversations.find(c => 
      c.user && c.user._id === user._id && 
      c.helper?.role === 'admin'
    );
    
    if (existing) {
      await loadConversation(existing._id);
    }
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedUserForMessage(null);
    setSelectedConversation(null);
    setMessages([]);
    setMessageText('');
  };

  const startConversation = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          userId: selectedUserForMessage._id,
          subject: 'Message from Lauft Team'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setConversations([...conversations, data.data]);
        await loadConversation(data.data._id);
      } else {
        setError(data.error || 'Failed to create conversation');
      }
    } catch (err) {
      setError('Error starting conversation: ' + err.message);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) return;

    if (!selectedConversation) {
      // Create conversation first
      await startConversation();
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${selectedConversation._id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ content: messageText.trim() })
      });

      const data = await response.json();
      
      if (data.success) {
        await loadConversation(selectedConversation._id);
        setMessageText('');
      } else {
        setError('Failed to send message');
      }
    } catch (err) {
      setError('Error sending message: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  // Curated matches functions
  const fetchProperties = async () => {
    try {
      const response = await propertyService.getProperties({ limit: 10000, page: 1 });
      
      if (response && response.data) {
        const availableProperties = response.data.filter(p => p.propertyStatus === 'For Sale');
        setProperties(availableProperties);
        setFilteredProperties(availableProperties);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  };

  const fetchExistingMatches = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/curated-matches`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        // Filter matches for this specific user
        const userMatches = data.data.filter(match => match.user._id === userId);
        setExistingMatches(userMatches);
      }
    } catch (err) {
      console.error('Error fetching existing matches:', err);
    }
  };

  const handlePropertySearch = (term) => {
    setPropertySearchTerm(term);
    if (!term.trim()) {
      setFilteredProperties(properties);
    } else {
      const filtered = properties.filter(property => 
        property.address.toLowerCase().includes(term.toLowerCase()) ||
        (typeof property.neighborhood === 'string' && property.neighborhood.toLowerCase().includes(term.toLowerCase())) ||
        (property.neighborhood?.name && property.neighborhood.name.toLowerCase().includes(term.toLowerCase()))
      );
      setFilteredProperties(filtered);
    }
  };

  const openCurateModal = async (user) => {
    setSelectedUserForCurate(user);
    setShowCurateModal(true);
    setSelectedProperties([]);
    setPropertySearchTerm('');
    setCurateView('existing'); // Start with existing matches view
    
    // Fetch existing matches for this user
    await fetchExistingMatches(user._id);
    
    // Fetch properties if not already loaded
    if (properties.length === 0) {
      await fetchProperties();
    }
  };

  const closeCurateModal = () => {
    setShowCurateModal(false);
    setSelectedUserForCurate(null);
    setSelectedProperties([]);
    setPropertySearchTerm('');
    setExistingMatches([]);
    setCurateView('existing');
  };

  const togglePropertySelection = (property) => {
    const isSelected = selectedProperties.find(p => p._id === property._id);
    if (isSelected) {
      setSelectedProperties(selectedProperties.filter(p => p._id !== property._id));
    } else {
      if (selectedProperties.length >= 6) {
        setError('Maximum 6 properties allowed per curated match');
        setTimeout(() => setError(''), 3000);
        return;
      }
      setSelectedProperties([...selectedProperties, property]);
    }
  };

  const handleSendCuratedMatch = async () => {
    if (!selectedUserForCurate || selectedProperties.length === 0) {
      setError('Please select at least one property');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setIsCurating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/curated-matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          userId: selectedUserForCurate._id,
          propertyIds: selectedProperties.map(p => p._id)
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`✅ Successfully sent ${selectedProperties.length} curated ${selectedProperties.length === 1 ? 'match' : 'matches'} to ${selectedUserForCurate.name}!`);
        setTimeout(() => setSuccess(''), 3000);
        setSelectedProperties([]);
        // Refresh existing matches
        await fetchExistingMatches(selectedUserForCurate._id);
        setCurateView('existing'); // Switch back to existing view
      } else {
        setError(data.error || 'Failed to send curated matches');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError('Error sending curated matches: ' + err.message);
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsCurating(false);
    }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this curated match?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/curated-matches/${matchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Curated match deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
        // Refresh existing matches
        await fetchExistingMatches(selectedUserForCurate._id);
      } else {
        setError('Failed to delete curated match');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError('Error deleting curated match: ' + err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  useEffect(() => {
    fetchUsers(1, '');
    fetchAgents();
  }, []);

  // US full-name -> 2-letter state code map (for normalizing location data)
  const US_STATE_CODES = {
    alabama:'AL', alaska:'AK', arizona:'AZ', arkansas:'AR', california:'CA', colorado:'CO',
    connecticut:'CT', delaware:'DE', florida:'FL', georgia:'GA', hawaii:'HI', idaho:'ID',
    illinois:'IL', indiana:'IN', iowa:'IA', kansas:'KS', kentucky:'KY', louisiana:'LA',
    maine:'ME', maryland:'MD', massachusetts:'MA', michigan:'MI', minnesota:'MN', mississippi:'MS',
    missouri:'MO', montana:'MT', nebraska:'NE', nevada:'NV', 'new hampshire':'NH', 'new jersey':'NJ',
    'new mexico':'NM', 'new york':'NY', 'north carolina':'NC', 'north dakota':'ND', ohio:'OH',
    oklahoma:'OK', oregon:'OR', pennsylvania:'PA', 'rhode island':'RI', 'south carolina':'SC',
    'south dakota':'SD', tennessee:'TN', texas:'TX', utah:'UT', vermont:'VT', virginia:'VA',
    washington:'WA', 'west virginia':'WV', wisconsin:'WI', wyoming:'WY', 'district of columbia':'DC',
  };
  const VALID_STATE_CODES = new Set(Object.values(US_STATE_CODES));

  // Normalize a state-ish token to its 2-letter code; returns null if not a US state
  const normalizeState = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const upper = trimmed.toUpperCase();
    if (upper.length === 2 && VALID_STATE_CODES.has(upper)) return upper;
    const lower = trimmed.toLowerCase();
    if (US_STATE_CODES[lower]) return US_STATE_CODES[lower];
    return null;
  };

  // Extract a 2-letter state code from any shape of user.location
  const stateOf = (u) => {
    if (!u) return null;
    const loc = u.location;
    // 1. Structured object: { city, state }
    if (loc && typeof loc === 'object' && !Array.isArray(loc)) {
      const s = normalizeState(loc.state);
      if (s) return s;
    }
    // 2. Plain string: try to pull the last comma-separated token
    if (typeof loc === 'string') {
      const parts = loc.split(',').map(t => t.trim()).filter(Boolean);
      for (let i = parts.length - 1; i >= 0; i--) {
        const s = normalizeState(parts[i]);
        if (s) return s;
      }
    }
    // 3. Flat field fallback
    return normalizeState(u.state);
  };

  // Cheap check that filters out empty strings and known broken placeholder URLs
  const hasValidProfileImage = (u) => {
    if (!u) return false;
    const src = u.profileImage;
    if (!src || typeof src !== 'string') return false;
    const trimmed = src.trim();
    if (!trimmed) return false;
    // Filter known placeholder/broken shapes
    const lower = trimmed.toLowerCase();
    if (lower.includes('placeholder') || lower.includes('default-avatar')) return false;
    return true;
  };

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* User Management — Control Center design system (matches CreateAgentForm) */

        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .admin-panel .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .admin-panel .card-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .admin-panel .search-container {
          background: #fff;
          border-radius: 12px;
          padding: 12px 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          border: 1px solid #e5e7eb;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .admin-panel .search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 13px;
          color: #111;
          background: transparent;
          font-family: inherit;
          padding: 0;
        }

        .admin-panel .search-input::placeholder { color: #9ca3af; }

        .admin-panel .alert-success {
          background-color: rgba(34, 197, 94, 0.08);
          color: #166534;
          border: 1px solid rgba(34, 197, 94, 0.25);
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25);
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .dashboard-card .table-container {
          margin: 0 -20px -20px;
          overflow-x: auto;
          overflow-y: auto;
          max-height: 560px;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar { width: 8px; height: 8px; }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-track { background: transparent; }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb:hover { background: #d1d5db; }

        .admin-panel .data-table {
          width: 100%;
          border-collapse: collapse;
          background: #fff;
          border-radius: 0;
          overflow: hidden;
          box-shadow: none;
          border: none;
          border-top: 1px solid #f0f0f0;
        }
        .admin-panel .data-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #fafafa;
          padding: 11px 14px;
          text-align: left;
          font-weight: 700;
          font-size: 10px;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border-bottom: 1px solid #f0f0f0;
        }
        .admin-panel .data-table td {
          padding: 12px 14px;
          border-bottom: 1px solid #f5f5f5;
          color: #111;
          font-size: 13px;
          text-align: left;
          vertical-align: middle;
        }
        .admin-panel .data-table tr:last-child td { border-bottom: none; }
        .admin-panel .data-table tbody tr:hover { background: #fafafa; }

        .admin-panel .user-cell {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          text-align: left;
        }
        .admin-panel .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          overflow: hidden;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .admin-panel .user-info {
          display: flex;
          flex-direction: column;
          min-width: 0;
          text-align: left;
          align-items: flex-start;
        }
        .admin-panel .user-name {
          font-size: 13px;
          font-weight: 600;
          color: #2563eb;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 240px;
          text-align: left;
        }
        .admin-panel .user-email {
          font-size: 11px;
          color: #6b7280;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 240px;
          text-align: left;
        }

        .admin-panel .pill {
          display: inline-block;
          padding: 2px 9px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          white-space: nowrap;
        }
        .admin-panel .pill-assigned   { background: rgba(37, 99, 235, 0.1); color: #1d4ed8; }
        .admin-panel .pill-unassigned { background: #f3f4f6; color: #9ca3af; }
        .admin-panel .pill-buy        { background: rgba(16, 185, 129, 0.12); color: #047857; }
        .admin-panel .pill-rent       { background: rgba(245, 158, 11, 0.14); color: #b45309; }

        .admin-panel .inline-assign {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .admin-panel .inline-assign select {
          padding: 4px 8px;
          border: 1px solid #e5e7eb;
          border-radius: 7px;
          font-size: 11px;
          background: #fff;
          color: #111;
          cursor: pointer;
          outline: none;
          max-width: 200px;
          font-family: inherit;
        }
        .admin-panel .inline-assign select:focus { border-color: #2563eb; }
        .admin-panel .inline-assign button {
          padding: 4px 10px;
          border-radius: 7px;
          border: none;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }
        .admin-panel .inline-assign .inline-save {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          box-shadow: 0 1px 3px rgba(37, 99, 235, 0.25);
        }
        .admin-panel .inline-assign .inline-save:disabled { opacity: 0.5; cursor: default; }
        .admin-panel .inline-assign .inline-cancel {
          background: transparent;
          color: #9ca3af;
          border: 1px solid #e5e7eb;
        }
        .admin-panel .inline-assign .inline-cancel:hover { color: #111; border-color: #d1d5db; }

        .admin-panel .row-actions {
          display: flex;
          gap: 6px;
          align-items: center;
        }
        .admin-panel .row-action-btn {
          padding: 5px 10px;
          border-radius: 7px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #6b7280;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.15s;
          font-family: inherit;
          white-space: nowrap;
        }
        .admin-panel .row-action-btn:hover {
          color: #2563eb;
          border-color: #2563eb;
          background: rgba(37, 99, 235, 0.04);
        }

        .admin-panel .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 20px;
        }
        .admin-panel .pagination-btn {
          padding: 7px 14px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          color: #6b7280;
          font-weight: 600;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }
        .admin-panel .pagination-btn:hover:not(:disabled) { background: #f9fafb; border-color: #2563eb; color: #2563eb; }
        .admin-panel .pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .admin-panel .page-info { padding: 7px 12px; color: #9ca3af; font-size: 12px; font-weight: 500; }

        .admin-panel .empty-state,
        .admin-panel .loading-state {
          text-align: center;
          padding: 48px 20px;
          color: #9ca3af;
          font-size: 13px;
        }
        .admin-panel .loading-state .spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #f0f0f0;
          border-top-color: #2563eb;
          border-radius: 50%;
          margin: 0 auto 12px;
          animation: um-spin 0.8s linear infinite;
        }
        @keyframes um-spin { to { transform: rotate(360deg); } }

        .admin-panel .modal-overlay {
          position: fixed; inset: 0;
          background: rgba(17, 17, 17, 0.5);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000;
        }
        .admin-panel .modal-content {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          width: 520px; max-width: 92vw; max-height: 90vh;
          overflow: hidden;
          display: flex; flex-direction: column;
        }
        .admin-panel .modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #f0f0f0;
        }
        .admin-panel .modal-header h3 { margin: 0; font-size: 16px; font-weight: 700; color: #111; }
        .admin-panel .modal-close {
          background: none; border: none; cursor: pointer;
          padding: 6px 10px; border-radius: 8px; color: #9ca3af;
          font-size: 20px; line-height: 1;
        }
        .admin-panel .modal-close:hover { background: #f3f4f6; color: #111; }
        .admin-panel .modal-body { padding: 24px; overflow-y: auto; flex: 1; background: #fff; }
        .admin-panel .modal-footer {
          padding: 16px 24px; border-top: 1px solid #f0f0f0;
          display: flex; justify-content: flex-end; gap: 10px; background: #fff;
        }

        .admin-panel .form-group { margin-bottom: 16px; }
        .admin-panel .form-label {
          display: block; color: #9ca3af; margin-bottom: 6px;
          font-weight: 600; font-size: 11px;
          text-transform: uppercase; letter-spacing: 0.05em;
        }
        .admin-panel .form-control {
          width: 100%; padding: 9px 12px;
          border: 1px solid #e5e7eb; border-radius: 9px;
          font-size: 13px; color: #111; background: #fff;
          outline: none; box-sizing: border-box; font-family: inherit;
        }
        .admin-panel .form-control:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12); }

        .admin-panel .btn {
          padding: 9px 18px; border-radius: 10px;
          font-weight: 600; cursor: pointer;
          transition: all 0.15s;
          font-size: 13px; letter-spacing: 0.01em;
          border: 1px solid #e5e7eb; background: #fff; color: #6b7280;
          font-family: inherit;
        }
        .admin-panel .btn:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .admin-panel .btn:disabled { opacity: 0.6; cursor: default; }
        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff; border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }
        .admin-panel .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          color: #fff;
        }
        .admin-panel .btn-secondary { background: #fff; color: #6b7280; border: 1px solid #e5e7eb; }
        .admin-panel .btn-secondary:hover { color: #111; }
        .admin-panel .ml-2 { margin-left: 8px; }

        .admin-panel .messages-container { max-height: 340px; overflow-y: auto; padding: 8px 0; }
        .admin-panel .message-item { margin-bottom: 10px; }
        .admin-panel .message-item.admin { text-align: right; }
      `}</style>
      
      {/* User HQ branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase' }}>User Management</span>
      </div>

      {error && <div className="alert-danger">{error}</div>}
      {success && <div className="alert-success">{success}</div>}

      {/* Search */}
      <div className="search-container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          type="text"
          placeholder="Search users by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {/* User Table */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>
            All Users
            <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '600', color: '#d1d5db', letterSpacing: '0.05em' }}>
              {totalUsers}
            </span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Opens a modal rather than expanding inline — this is a weekly
                action and it shouldn't cost the table any vertical space. */}
            <button
              type="button"
              onClick={openCsSends}
              title="Bulk-send recent users with no swipes and no agent to the CS dashboard"
              style={{
                padding: '5px 12px',
                borderRadius: '7px',
                border: '1px solid #e5e7eb',
                background: '#fff',
                fontSize: '11px',
                fontWeight: '600',
                color: '#6b7280',
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              CS Sends
            </button>
            <select
              value={searchTypeFilter}
              onChange={e => setSearchTypeFilter(e.target.value)}
              style={{
                padding: '5px 28px 5px 10px',
                borderRadius: '7px',
                border: '1px solid #e5e7eb',
                background: '#fff',
                fontSize: '11px',
                fontWeight: '600',
                color: searchTypeFilter === 'all' ? '#6b7280' : '#2563eb',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                letterSpacing: '0.02em',
              }}
              title="Filter by search type"
            >
              <option value="all">All Types</option>
              <option value="buy">Buy</option>
              <option value="rent">Rent</option>
            </select>
            {(() => {
              // Derive unique state options from the currently loaded users (filteredUsers = current page)
              const set = new Set();
              filteredUsers.forEach(u => { const s = stateOf(u); if (s) set.add(s); });
              const states = Array.from(set).sort();
              return (
                <select
                  value={stateFilter}
                  onChange={e => setStateFilter(e.target.value)}
                  style={{
                    padding: '5px 28px 5px 10px',
                    borderRadius: '7px',
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: stateFilter === 'all' ? '#6b7280' : '#2563eb',
                    cursor: 'pointer',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    letterSpacing: '0.02em',
                  }}
                  title="Filter by state"
                  disabled={states.length === 0}
                >
                  <option value="all">All States</option>
                  {states.map(s => (<option key={s} value={s}>{s}</option>))}
                </select>
              );
            })()}
          </div>
        </div>

        {(() => {
          if (isLoading) {
            return (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading users...</p>
              </div>
            );
          }
          // Apply header filters client-side to the current page.
          // Note: this narrows visible rows only — filtering across all pages requires server-side support.
          const visibleUsers = filteredUsers.filter(u => {
            if (searchTypeFilter !== 'all' && (u.lookingFor || '').toLowerCase() !== searchTypeFilter) return false;
            if (stateFilter !== 'all' && stateOf(u) !== stateFilter) return false;
            return true;
          });
          if (visibleUsers.length === 0) {
            return (
              <div className="empty-state">
                <p>No users found. Try adjusting your search or filters.</p>
              </div>
            );
          }
          return (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '240px' }}>User</th>
                  <th>Tier</th>
                  <th>Assigned</th>
                  <th>Search</th>
                  <th>Swipes</th>
                  <th>Location</th>
                  <th>Joined</th>
                  <th>CS</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map(user => {
                  const isInlineOpen = inlineAssignUserId === user._id;
                  const swipes = user.totalSwipes ?? user.swipesCount ?? user.swipes ?? null;
                  const lookingFor = (user.lookingFor || '').toLowerCase();
                  return (
                    <tr key={user._id}>
                      <td style={{ minWidth: '240px' }}>
                        <div className="user-cell" onClick={() => openBuyerDetails(user)} title="Click to view user details">
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                          <div className="user-avatar" style={{ background: hasValidProfileImage(user) ? 'transparent' : '#f3f4f6', position: 'relative' }}>
                            {/* Always render the placeholder SVG as the base layer — the <img> sits on top and disappears on error, revealing the placeholder */}
                            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
                              <rect width="40" height="40" fill="#e5e7eb" rx="20"/>
                              <circle cx="20" cy="15" r="7" fill="#9ca3af"/>
                              <path d="M4 36c0-8.837 7.163-16 16-16s16 7.163 16 16" fill="#9ca3af"/>
                            </svg>
                            {hasValidProfileImage(user) && (
                              <img
                                src={user.profileImage}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', position: 'relative', zIndex: 1 }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                          </div>
                            {user.hasBotChat && (
                              <span
                                title="Has chatted with the Lauft assistant"
                                style={{
                                  position: 'absolute',
                                  bottom: '-3px',
                                  right: '-3px',
                                  width: '19px',
                                  height: '19px',
                                  borderRadius: '50%',
                                  backgroundColor: '#ece9fb',
                                  border: '2px solid #fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 3,
                                  boxSizing: 'border-box',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                }}
                              >
                                <img
                                  src={darkIcon}
                                  alt="Bot"
                                  style={{ width: '11px', height: '11px', objectFit: 'contain' }}
                                />
                              </span>
                            )}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{user.name}</span>
                            <span className="user-email">{user.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {user.leadTier ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '3px 9px',
                              borderRadius: '999px',
                              fontSize: '12px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              backgroundColor: (TIER_COLORS[user.leadTier] || {}).bg || '#f3f4f6',
                              color: (TIER_COLORS[user.leadTier] || {}).text || '#6b7280',
                            }}
                          >
                            <span>{TIER_EMOJI[user.leadTier] || '⭐'}</span>
                            <span>{tierLabel(user.leadTier)}</span>
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td>
                        {isInlineOpen ? (
                          <div className="inline-assign">
                            <AgentSearchSelect
                              agents={agents}
                              value={inlineSelectedAgentId}
                              onChange={setInlineSelectedAgentId}
                              placeholder="Search agents..."
                              autoFocus
                            />
                            <button
                              className="inline-save"
                              onClick={() => handleInlineAssign(user)}
                              disabled={isAssigning || !inlineSelectedAgentId}
                            >
                              {isAssigning ? '...' : 'Save'}
                            </button>
                            <button className="inline-cancel" onClick={closeInlineAssign}>Cancel</button>
                            {user.assignedAgent && (
                              <button
                                className="inline-cancel"
                                onClick={() => { closeInlineAssign(); removeAssignment(user._id, user.name); }}
                                style={{ color: '#dc2626', borderColor: 'rgba(220,38,38,0.25)' }}
                                title="Remove assignment"
                              >
                                Unassign
                              </button>
                            )}
                          </div>
                        ) : user.assignedAgent ? (
                          <button
                            className="pill pill-assigned"
                            onClick={() => openInlineAssign(user)}
                            style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                            title={`Assigned to ${user.assignedAgent.name} - click to change`}
                          >
                            {user.assignedAgent.name}
                          </button>
                        ) : (
                          <button
                            className="pill pill-unassigned"
                            onClick={() => openInlineAssign(user)}
                            style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                            title="Click to assign an agent"
                          >
                            Unassigned
                          </button>
                        )}
                      </td>
                      <td>
                        {lookingFor === 'buy' ? (
                          <span className="pill pill-buy">Buy</span>
                        ) : lookingFor === 'rent' ? (
                          <span className="pill pill-rent">Rent</span>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#d1d5db' }}>—</span>
                        )}
                      </td>
                      <td>
                        {swipes !== null && swipes !== undefined ? (
                          swipes > 0 ? (
                            <button
                              type="button"
                              onClick={() => openBuyerDetails(user)}
                              title="View swipe activity"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                color: '#2563eb',
                                fontWeight: 700,
                                fontSize: 'inherit',
                                fontFamily: 'inherit',
                                textDecoration: 'none',
                                borderBottom: '1px dashed transparent',
                                transition: 'border-color 0.15s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.borderBottomColor = '#2563eb'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.borderBottomColor = 'transparent'; }}
                            >
                              {swipes}
                            </button>
                          ) : (
                            <strong style={{ color: '#d1d5db' }}>{swipes}</strong>
                          )
                        ) : (
                          <span style={{ fontSize: '11px', color: '#d1d5db' }}>—</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          // Resolve user location across possible shapes:
                          //   - plain string:           user.location = "Denver, CO"
                          //   - city/state object:       user.location = { city, state }
                          //   - GeoJSON Point:           user.location = { type: 'Point', coordinates: [...] }
                          //   - flat fields on user:     user.city, user.state
                          let label = null;
                          const loc = user.location;
                          if (typeof loc === 'string' && loc.trim()) {
                            label = loc.trim();
                          } else if (loc && typeof loc === 'object') {
                            const city = loc.city || loc.name || '';
                            const state = loc.state || loc.region || '';
                            if (city || state) label = [city, state].filter(Boolean).join(', ');
                          }
                          if (!label && (user.city || user.state)) {
                            label = [user.city, user.state].filter(Boolean).join(', ');
                          }
                          return label ? (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 7px',
                              borderRadius: '999px',
                              fontSize: '9px',
                              fontWeight: '600',
                              color: '#6b7280',
                              background: '#f3f4f6',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              whiteSpace: 'nowrap',
                            }}>
                              {label}
                            </span>
                          ) : (
                            <span style={{ fontSize: '10px', color: '#d1d5db' }}>—</span>
                          );
                        })()}
                      </td>
                      <td>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const cs = user.csSync || {};
                          const isBusy = csBusyId === user._id;
                          const inCs = Boolean(cs.pushedAt);

                          if (inCs) {
                            const pushedOn = new Date(cs.pushedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            return (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span
                                  title={`Sent to CS ${pushedOn}${cs.pushedBy ? ` by ${cs.pushedBy}` : ''}${cs.lastError ? `\nLast push failed: ${cs.lastError}` : ''}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    padding: '3px 9px',
                                    borderRadius: '999px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    backgroundColor: cs.lastError ? '#fef3c7' : '#e8f5e9',
                                    color: cs.lastError ? '#92400e' : '#2e7d32',
                                  }}
                                >
                                  {cs.lastError ? '⚠️' : '✓'} In CS
                                </span>
                                {/* Refresh — re-sends the current snapshot so a
                                    CSA isn't reading a stale tier or agent. */}
                                <button
                                  type="button"
                                  onClick={() => pushUserToCs(user)}
                                  disabled={isBusy}
                                  title="Re-send this user's current details to CS"
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '2px 3px',
                                    cursor: isBusy ? 'default' : 'pointer',
                                    color: '#6b7280',
                                    fontSize: '12px',
                                    lineHeight: 1,
                                  }}
                                >
                                  {isBusy ? '…' : '↻'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeUserFromCs(user)}
                                  disabled={isBusy}
                                  title="Remove from the CS dashboard"
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '2px 3px',
                                    cursor: isBusy ? 'default' : 'pointer',
                                    color: '#dc2626',
                                    fontSize: '12px',
                                    lineHeight: 1,
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            );
                          }

                          return (
                            <button
                              type="button"
                              onClick={() => pushUserToCs(user)}
                              disabled={isBusy}
                              title="Send this user's contact info, details and activity to the CS dashboard"
                              style={{
                                padding: '3px 10px',
                                borderRadius: '999px',
                                fontSize: '12px',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                fontFamily: 'inherit',
                                cursor: isBusy ? 'default' : 'pointer',
                                border: '1px solid #d1d5db',
                                background: '#fff',
                                color: '#4b5563',
                              }}
                            >
                              {isBusy ? 'Sending…' : 'Send to CS'}
                            </button>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          );
        })()}
      </div>

      {/* Pagination */}
      {!isLoading && filteredUsers.length > 0 && totalPages > 1 && (
        <div className="pagination-container">
          <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1} title="First page">«</button>
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
          <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} title="Last page">»</button>
        </div>
      )}

      {/* CS Sends Modal */}
      {showCsSendsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>CS Sends</h3>
              <button onClick={closeCsSends} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 14px' }}>
                Users who joined recently, never swiped, and have no agent. Already-sent
                users are excluded.
              </p>

              <div className="form-group">
                <label className="form-label">Joined in the last</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {[3, 7, 14, 30].map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => changeCsSendsDays(d)}
                      disabled={csBulkRunning}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '7px',
                        fontSize: '12px',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        cursor: csBulkRunning ? 'default' : 'pointer',
                        border: `1px solid ${csSendsDays === d ? '#2563eb' : '#e5e7eb'}`,
                        background: csSendsDays === d ? '#eff6ff' : '#fff',
                        color: csSendsDays === d ? '#2563eb' : '#6b7280',
                      }}
                    >
                      {d} days
                    </button>
                  ))}
                </div>
              </div>

              {csCandidatesLoading && (
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>Checking…</p>
              )}

              {!csCandidatesLoading && csCandidates && (
                <>
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: csCandidates.total > 0 ? '#f9fafb' : '#f3f4f6',
                    marginBottom: '10px',
                  }}>
                    <strong style={{ fontSize: '15px', color: '#111827' }}>
                      {csCandidates.total}
                    </strong>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {' '}user{csCandidates.total === 1 ? '' : 's'} match
                    </span>
                    {csCandidates.truncated && (
                      <div style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                        ⚠️ Only the {csCandidates.limit} most recent will send. Narrow the
                        day range and run it again for the rest.
                      </div>
                    )}
                  </div>

                  {csCandidates.users.length > 0 && (
                    <div style={{
                      maxHeight: '220px',
                      overflowY: 'auto',
                      border: '1px solid #f3f4f6',
                      borderRadius: '8px',
                    }}>
                      {csCandidates.users.map(u => (
                        <div
                          key={u._id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '7px 10px',
                            borderBottom: '1px solid #f9fafb',
                            fontSize: '12px',
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {u.name}
                            </div>
                            <div style={{ color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {u.email}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            {/* CS can't message a user who never gave contact
                                consent, so flag it here rather than letting the
                                CSA find out on the card. */}
                            {!u.contactConsent && (
                              <span title="No contact consent — CS cannot message this user" style={{ color: '#92400e' }}>
                                ⚠️
                              </span>
                            )}
                            <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>
                              {new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {csBulkResult && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  background: csBulkResult.failedCount > 0 ? '#fef3c7' : '#e8f5e9',
                  fontSize: '12px',
                  color: csBulkResult.failedCount > 0 ? '#92400e' : '#2e7d32',
                }}>
                  <strong>{csBulkResult.pushed} sent</strong>
                  {csBulkResult.failedCount > 0 && <> · {csBulkResult.failedCount} failed</>}
                  {csBulkResult.aborted && (
                    <div style={{ marginTop: '4px' }}>
                      Stopped early after repeated failures — the CS dashboard is
                      probably down. The rest are untouched, so re-run when it's back.
                    </div>
                  )}
                  {csBulkResult.failed && csBulkResult.failed.length > 0 && (
                    <div style={{ marginTop: '6px', maxHeight: '90px', overflowY: 'auto' }}>
                      {csBulkResult.failed.map(f => (
                        <div key={f.userId} style={{ marginTop: '2px' }}>
                          {f.name} — {f.detail}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={closeCsSends} className="btn btn-secondary" disabled={csBulkRunning}>
                Close
              </button>
              <button
                onClick={runCsBulkPush}
                className="btn btn-primary"
                disabled={csBulkRunning || csCandidatesLoading || !csCandidates || csCandidates.total === 0}
              >
                {csBulkRunning
                  ? 'Sending…'
                  : `Send ${csCandidates ? Math.min(csCandidates.total, csCandidates.limit) : 0} to CS`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign Agent to {selectedUser?.name}</h3>
              <button onClick={closeAssignModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Select Agent</label>
                <AgentSearchSelect
                  agents={agents}
                  value={selectedAgentId}
                  onChange={setSelectedAgentId}
                  placeholder="Search by name or agency..."
                  className="form-group-agent-search"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={closeAssignModal}
                className="btn btn-secondary"
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignment}
                className="btn btn-primary ml-2"
                disabled={isAssigning || !selectedAgentId}
              >
                {isAssigning ? 'Assigning...' : 'Assign Agent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin User Modal */}
      <AdminUserModal
        user={selectedBuyerForDetails}
        isOpen={showBuyerDetailsModal}
        onClose={closeBuyerDetails}
        onUserUpdated={fetchUsers}
        onPoolChanged={patchUserPool}
      />

      {/* Message Modal */}
      {showMessageModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: '600px', maxWidth: '90vw' }}>
            <div className="modal-header">
              <h3>Message {selectedUserForMessage?.name}</h3>
              <button onClick={closeMessageModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                <div className="messages-container">
                  {messages.map((msg, idx) => (
                    <div key={idx} className={`message-item ${msg.sender.role === 'admin' ? 'admin' : 'user'}`}>
                      <div className={`message-bubble ${msg.sender.role === 'admin' ? 'admin' : 'user'}`}>
                        {msg.content}
                      </div>
                      <div className="message-time">
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
              <div className="message-input-container">
                <textarea
                  className="message-input"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  rows={3}
                />
                <button
                  onClick={sendMessage}
                  className="btn btn-primary"
                  disabled={isSending || !messageText.trim()}
                  style={{ alignSelf: 'flex-end' }}
                >
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeMessageModal} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Curate Matches Modal */}
      {showCurateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: '900px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3>Curate Matches for {selectedUserForCurate?.name}</h3>
              <button onClick={closeCurateModal} className="modal-close">×</button>
            </div>
            <div className="modal-body" style={{ flex: 1, overflow: 'auto' }}>
              {/* Tabs */}
              <div className="tabs-container">
                <button
                  className={`tab-button ${curateView === 'existing' ? 'active' : ''}`}
                  onClick={() => setCurateView('existing')}
                >
                  Existing Matches ({existingMatches.length})
                </button>
                <button
                  className={`tab-button ${curateView === 'create' ? 'active' : ''}`}
                  onClick={() => setCurateView('create')}
                >
                  Create New Match
                </button>
              </div>

              {/* Existing Matches View */}
              {curateView === 'existing' && (
                <div className="existing-matches-list">
                  {existingMatches.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        <Package size={64} color="rgba(255, 255, 255, 0.2)" strokeWidth={1.5} />
                      </div>
                      <div className="empty-state-title">
                        No curated matches yet
                      </div>
                      <div className="empty-state-text">
                        Click "Create New Match" to send property recommendations to this user
                      </div>
                    </div>
                  ) : (
                    existingMatches.map(match => {
                      const now = new Date();
                      const expiresAt = new Date(match.expiresAt);
                      const isExpired = match.status === 'expired' || now > expiresAt;
                      const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
                      const viewedCount = match.viewedProperties ? match.viewedProperties.length : 0;
                      
                      return (
                        <div key={match._id} className="match-item">
                          <div className="match-header">
                            <div>
                              <span className={`status-badge-match ${isExpired ? 'badge-expired' : 'badge-active'}`}>
                                {isExpired ? 'Expired' : `${daysLeft}d left`}
                              </span>
                            </div>
                            <div className="match-stats">
                              <span>📦 {match.properties.length} properties</span>
                              <span>👁️ {viewedCount} / {match.properties.length} viewed</span>
                              <span>📅 {new Date(match.createdAt).toLocaleDateString()}</span>
                            </div>
                            <button
                              onClick={() => handleDeleteMatch(match._id)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              Delete
                            </button>
                          </div>
                          
                          <div className="match-properties">
                            {match.properties.map(property => (
                              <div key={property._id} className="match-property-card">
                                <img
                                  src={property.mainImage || 'https://via.placeholder.com/150x100?text=No+Image'}
                                  alt={property.address}
                                  className="match-property-image"
                                />
                                <div className="match-property-info">
                                  <div className="match-property-address">{property.address}</div>
                                  <div className="match-property-price">{property.price}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Create New Match View */}
              {curateView === 'create' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Search Properties</label>
                    <input
                      type="text"
                      className="form-control"
                      value={propertySearchTerm}
                      onChange={(e) => handlePropertySearch(e.target.value)}
                      placeholder="Search by address or neighborhood..."
                    />
                  </div>
                  
                  {selectedProperties.length > 0 && (
                    <div className="selected-count">
                      {selectedProperties.length} of 6 properties selected
                    </div>
                  )}
                  
                  <div className="properties-grid">
                    {filteredProperties.map(property => {
                      const isSelected = selectedProperties.find(p => p._id === property._id);
                      return (
                        <div
                          key={property._id}
                          className={`property-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => togglePropertySelection(property)}
                        >
                          <img
                            src={property.mainImage || 'https://via.placeholder.com/200x120?text=No+Image'}
                            alt={property.address}
                            className="property-image"
                          />
                          <div className="property-info-card">
                            <div className="property-address">{property.address}</div>
                            <div className="property-price">{property.price}</div>
                          </div>
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: '#6366f1',
                              color: 'white',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              ✓
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {filteredProperties.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.6)' }}>
                      No properties found
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={closeCurateModal} className="btn btn-secondary" disabled={isCurating}>
                Close
              </button>
              {curateView === 'create' && (
                <button
                  onClick={handleSendCuratedMatch}
                  className="btn btn-primary ml-2"
                  disabled={isCurating || selectedProperties.length === 0}
                >
                  {isCurating ? 'Sending...' : `Send ${selectedProperties.length} ${selectedProperties.length === 1 ? 'Match' : 'Matches'}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAssignmentManager;